import React, { useState, useEffect, useMemo } from "react";

import { BaseTextFieldProps } from "@mui/material";
import Stack from "@mui/material/Stack";
import { isEqual } from "date-fns";
import { format } from "date-fns-tz";
import { Range, RangeKeyDict, DateRange } from "react-date-range";
import { FiCalendar, FiX } from "react-icons/fi";

import Input from "../index";
import useStyles, { StyledMenu } from "./styles";

interface InputDateRangeProps extends BaseTextFieldProps {
  name?: string;
  label?: React.ReactNode;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  min?: string | number;
  max?: string | number;
  pStartDate?: Date | undefined;
  pEndDate?: Date | undefined;
  onChange?: (range: Range) => void;
}

const InputDateRange = (props: InputDateRangeProps) => {
  const {
    name,
    label,
    error,
    errorMessage,
    disabled = false,
    min,
    max,
    pStartDate,
    pEndDate,
    onChange,
    ...rest
  } = props;

  const classes = useStyles();

  const [rangeText, setRangeText] = useState<string>("");

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open: boolean = !!anchorEl;

  const [range, setRange] = useState<Range[]>([
    {
      startDate: pStartDate,
      endDate: pEndDate,
      key: "selection",
    },
  ]);

  useEffect(() => {
    if (!pStartDate && !pEndDate) {
      setRangeText("");
      setRange([
        {
          startDate: undefined,
          endDate: undefined,
          key: "selection",
        },
      ]);
    }
  }, [pStartDate, pEndDate]);

  const hasSelectedRange = useMemo(
    () => range && !!range.length && range[0].startDate && range[0].endDate,
    [range]
  );

  const checkBothDatesAreSelected = (selectedRange: Range): boolean => {
    return (
      !!selectedRange?.startDate &&
      !!selectedRange?.endDate &&
      !isEqual(selectedRange.startDate, selectedRange.endDate)
    );
  };

  useEffect(() => {
    const selectedRange = range[0];

    if (!selectedRange) setRangeText("");

    if (!checkBothDatesAreSelected(selectedRange)) {
      if (selectedRange?.startDate) {
        const formattedStartDate = format(selectedRange.startDate, "dd/MM/yyyy");

        setRangeText(`${formattedStartDate} - dd/mm/yyyy`);
      }
    } else {
      const formattedStartDate = format(selectedRange.startDate || new Date(), "dd/MM/yyyy");
      const formattedEndDate = format(selectedRange.endDate || new Date(), "dd/MM/yyyy");

      setRangeText(`${formattedStartDate} - ${formattedEndDate}`);
    }
  }, [range]);

  useEffect(() => {
    if (onChange && checkBothDatesAreSelected(range[0])) {
      onChange(range[0]);
      setAnchorEl(null);
    }
  }, [range]);

  const handleSelectRange = (item: RangeKeyDict) => {
    setRange([item.selection]);
  };

  const handleClearDateRange = () => {
    setAnchorEl(null);
    setRangeText("");
    setRange([
      {
        startDate: undefined,
        endDate: undefined,
        key: "selection",
      },
    ]);
    if (onChange) onChange({ startDate: undefined, endDate: undefined });
  };

  const endAdornment = (
    <Stack className={classes.endAdornment} direction="row" spacing={1}>
      {hasSelectedRange && <FiX className={classes.clearDateIcon} onClick={handleClearDateRange} />}
      <FiCalendar />
    </Stack>
  );

  const handleOpenDatePicker = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setAnchorEl(event.currentTarget);
  };

  return (
    <>
      <Input
        id="date-range-picker-input"
        name={name}
        label={label}
        value={rangeText}
        onClickCapture={handleOpenDatePicker}
        formControlClassName={classes.input}
        rightIcon={endAdornment}
        placeholder="dd/mm/yyyy - dd/mm/yyyy"
        error={error}
        errorMessage={errorMessage}
        disabled={disabled}
        {...rest}
      />
      <StyledMenu
        id="date-range-picker"
        anchorEl={anchorEl}
        keepMounted
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <DateRange
          showDateDisplay={false}
          preventSnapRefocus
          ranges={range}
          onChange={handleSelectRange}
          minDate={min ? new Date(min) : undefined}
          maxDate={max ? new Date(max) : undefined}
        />
      </StyledMenu>
    </>
  );
};

export default InputDateRange;
